// ─────────────────────────────────────────────────────────────────────────────
//  DairyPro Offline DB  —  src/utils/offlineDB.js
//  Single IndexedDB with all stores needed for offline mode
// ─────────────────────────────────────────────────────────────────────────────

const DB_NAME    = 'DairyProOffline';
const DB_VERSION = 3;   // bump if schema changes

// ── Store names ───────────────────────────────────────────────────────────────
export const STORES = {
  AUTH:         'auth_cache',         // { id:'current', user, hashedPw, tokens }
  FARMERS:      'farmers',            // { farmerId, farmerCode, farmerName, milkTypeId, ... }
  RATE_CHARTS:  'rate_charts',        // { milkTypeId, ...chartData }
  COLL_QUEUE:   'collections_queue',  // pending/synced offline collections
  SMS_QUEUE:    'sms_queue',          // pending SMS to send on reconnect
  SETTINGS:     'settings_cache',     // { key, value }
};

// ── Open / upgrade DB ─────────────────────────────────────────────────────────
let _db = null;

export function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db  = e.target.result;
      // auth_cache
      if (!db.objectStoreNames.contains(STORES.AUTH))
        db.createObjectStore(STORES.AUTH, { keyPath: 'id' });

      // farmers  — keyed by farmerId, index on farmerCode
      if (!db.objectStoreNames.contains(STORES.FARMERS)) {
        const fs = db.createObjectStore(STORES.FARMERS, { keyPath: 'farmerId' });
        fs.createIndex('byCode', 'farmerCode', { unique: false });
      }

      // rate_charts  — keyed by milkTypeId
      if (!db.objectStoreNames.contains(STORES.RATE_CHARTS))
        db.createObjectStore(STORES.RATE_CHARTS, { keyPath: 'milkTypeId' });

      // collections_queue  — auto-increment localId
      if (!db.objectStoreNames.contains(STORES.COLL_QUEUE)) {
        const qs = db.createObjectStore(STORES.COLL_QUEUE, { keyPath: 'localId', autoIncrement: true });
        qs.createIndex('byStatus', 'status', { unique: false });
      }

      // sms_queue
      if (!db.objectStoreNames.contains(STORES.SMS_QUEUE)) {
        const ss = db.createObjectStore(STORES.SMS_QUEUE, { keyPath: 'localId', autoIncrement: true });
        ss.createIndex('byStatus', 'status', { unique: false });
      }

      // settings_cache
      if (!db.objectStoreNames.contains(STORES.SETTINGS))
        db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
    };

    req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
    req.onerror   = (e) => reject(e.target.error);
  });
}

// ── Generic helpers ───────────────────────────────────────────────────────────
async function tx(storeName, mode, fn) {
  const db    = await openDB();
  const store = db.transaction(storeName, mode).objectStore(storeName);
  return fn(store);
}

function promiseReq(req) {
  return new Promise((res, rej) => {
    req.onsuccess = () => res(req.result);
    req.onerror   = () => rej(req.error);
  });
}

// ── AUTH CACHE ────────────────────────────────────────────────────────────────

/**
 * Hash a password for local comparison (SHA-256, hex string).
 * Not for security — just to avoid storing plaintext in IndexedDB.
 */
export async function hashPassword(pw) {
  const buf  = new TextEncoder().encode(pw);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Save successful login data so offline login works later.
 */
export async function saveAuthCache(userData, accessToken, refreshToken, plainPassword) {
  const hashedPw = await hashPassword(plainPassword);
  await tx(STORES.AUTH, 'readwrite', (store) =>
    promiseReq(store.put({
      id:           'current',
      user:         userData,
      hashedPw,
      accessToken,
      refreshToken,
      savedAt:      new Date().toISOString(),
    }))
  );
}

/**
 * Try offline login. Returns { success, user, accessToken, refreshToken } or { success: false }.
 */
export async function tryOfflineLogin(mobile, password) {
  try {
    const record = await tx(STORES.AUTH, 'readonly', (store) => promiseReq(store.get('current')));
    if (!record) return { success: false, reason: 'No offline session cached. Please login online first.' };

    if (record.user?.mobileNumber !== mobile)
      return { success: false, reason: 'Mobile number does not match cached session.' };

    const hashedPw = await hashPassword(password);
    if (record.hashedPw !== hashedPw)
      return { success: false, reason: 'Incorrect password.' };

    return {
      success:      true,
      user:         record.user,
      accessToken:  record.accessToken,
      refreshToken: record.refreshToken,
    };
  } catch {
    return { success: false, reason: 'Offline login error.' };
  }
}

// ── FARMER CACHE ──────────────────────────────────────────────────────────────

/**
 * Replace all cached farmers with a fresh list from API.
 */
export async function saveFarmers(farmersArray) {
  const db    = await openDB();
  const store = db.transaction(STORES.FARMERS, 'readwrite').objectStore(STORES.FARMERS);
  await promiseReq(store.clear());
  for (const f of farmersArray) await promiseReq(store.put(f));
}

/**
 * Look up a farmer by their code (case-insensitive).
 */
export async function getFarmerByCode(code) {
  const db    = await openDB();
  const store = db.transaction(STORES.FARMERS, 'readonly').objectStore(STORES.FARMERS);
  const index = store.index('byCode');
  const upper = code.toUpperCase();
  const all   = await promiseReq(index.getAll(upper));
  return all[0] || null;
}

/**
 * Get all farmers.
 */
export async function getAllFarmers() {
  return tx(STORES.FARMERS, 'readonly', (store) => promiseReq(store.getAll()));
}

// ── RATE CHART CACHE ──────────────────────────────────────────────────────────

/**
 * Save rate chart for a milk type (1=Cow, 2=Buffalo).
 */
export async function saveRateChart(milkTypeId, chartData) {
  await tx(STORES.RATE_CHARTS, 'readwrite', (store) =>
    promiseReq(store.put({ milkTypeId, ...chartData, savedAt: new Date().toISOString() }))
  );
}

/**
 * Get rate chart for a milk type.
 */
export async function getRateChart(milkTypeId) {
  return tx(STORES.RATE_CHARTS, 'readonly', (store) => promiseReq(store.get(milkTypeId)));
}

/**
 * Calculate rate offline using FAT/SNF step chart.
 * Mirrors the backend FAT+SNF step calculation exactly.
 */
export function calcRateOffline(chart, fat, snf) {
  if (!chart || !chart.fatSteps || !chart.snfSteps) return null;

  const { startingAmount, adjustmentValue, fatSteps, snfSteps } = chart;
  const base = parseFloat(startingAmount) || 0;
  const adj  = parseFloat(adjustmentValue) || 0;

  function calcInc(target, steps) {
    let inc = 0;
    const sorted = [...steps].sort((a, b) => a.fatValue - b.fatValue || a.snfValue - b.snfValue);
    for (let i = 0; i < sorted.length - 1; i++) {
      const from = parseFloat(sorted[i].fatValue ?? sorted[i].snfValue);
      const to   = parseFloat(sorted[i + 1].fatValue ?? sorted[i + 1].snfValue);
      const amt  = parseFloat(sorted[i].stepAmount);
      if (target <= from) break;
      const eff   = Math.min(target, to);
      const count = Math.round((eff - from) * 10);
      inc        += count * amt;
    }
    return inc;
  }

  const fatInc = calcInc(parseFloat(fat), fatSteps.map(s => ({ fatValue: s.fatValue, stepAmount: s.stepAmount })));
  const snfInc = calcInc(parseFloat(snf), snfSteps.map(s => ({ snfValue: s.snfValue, stepAmount: s.stepAmount })));

  const rate = base + fatInc + snfInc + adj;
  return Math.round(rate * 10000) / 10000;  // 4 decimal places
}

// ── COLLECTIONS QUEUE ─────────────────────────────────────────────────────────

export async function queueCollection(payload) {
  return tx(STORES.COLL_QUEUE, 'readwrite', (store) =>
    promiseReq(store.add({ ...payload, status: 'pending', createdAt: new Date().toISOString() }))
  );
}

export async function getAllCollectionQueue() {
  return tx(STORES.COLL_QUEUE, 'readonly', (store) => promiseReq(store.getAll()));
}

export async function updateCollectionQueueEntry(localId, updates) {
  const db    = await openDB();
  const store = db.transaction(STORES.COLL_QUEUE, 'readwrite').objectStore(STORES.COLL_QUEUE);
  const rec   = await promiseReq(store.get(localId));
  await promiseReq(store.put({ ...rec, ...updates }));
}

export async function clearSyncedCollections() {
  const db    = await openDB();
  const store = db.transaction(STORES.COLL_QUEUE, 'readwrite').objectStore(STORES.COLL_QUEUE);
  const all   = await promiseReq(store.getAll());
  for (const r of all.filter(r => r.status === 'synced')) await promiseReq(store.delete(r.localId));
}

// ── SMS QUEUE ────────────────────────────────────────────────────────────────

export async function queueSms(payload) {
  return tx(STORES.SMS_QUEUE, 'readwrite', (store) =>
    promiseReq(store.add({ ...payload, status: 'pending', createdAt: new Date().toISOString() }))
  );
}

export async function getAllSmsQueue() {
  return tx(STORES.SMS_QUEUE, 'readonly', (store) => promiseReq(store.getAll()));
}

export async function updateSmsQueueEntry(localId, updates) {
  const db    = await openDB();
  const store = db.transaction(STORES.SMS_QUEUE, 'readwrite').objectStore(STORES.SMS_QUEUE);
  const rec   = await promiseReq(store.get(localId));
  await promiseReq(store.put({ ...rec, ...updates }));
}

export async function clearSyncedSms() {
  const db    = await openDB();
  const store = db.transaction(STORES.SMS_QUEUE, 'readwrite').objectStore(STORES.SMS_QUEUE);
  const all   = await promiseReq(store.getAll());
  for (const r of all.filter(r => r.status === 'sent')) await promiseReq(store.delete(r.localId));
}

// ── SETTINGS CACHE ────────────────────────────────────────────────────────────

export async function saveSetting(key, value) {
  await tx(STORES.SETTINGS, 'readwrite', (store) =>
    promiseReq(store.put({ key, value }))
  );
}

export async function getSetting(key) {
  const rec = await tx(STORES.SETTINGS, 'readonly', (store) => promiseReq(store.get(key)));
  return rec?.value ?? null;
}

// ── SYNC ENGINE ───────────────────────────────────────────────────────────────

/**
 * Sync pending collections to the server.
 * Returns { synced, failed }.
 */
export async function syncCollections(api) {
  const all     = await getAllCollectionQueue();
  const pending = all.filter(e => e.status === 'pending');
  let synced = 0, failed = 0;

  for (const entry of pending) {
    const { localId, status, createdAt, syncedAt, error: _e, farmerName: _fn, rateSource: _rs, ...payload } = entry;
    try {
      const res = await api.post('/collection', payload);
      if (res.data.success) {
        await updateCollectionQueueEntry(localId, { status: 'synced', syncedAt: new Date().toISOString() });
        synced++;
      } else {
        await updateCollectionQueueEntry(localId, { status: 'failed', error: res.data.message });
        failed++;
      }
    } catch {
      await updateCollectionQueueEntry(localId, { status: 'failed', error: 'Network error' });
      failed++;
    }
  }

  await clearSyncedCollections();
  return { synced, failed };
}

/**
 * Sync pending SMS queue.
 */
export async function syncSmsQueue(api) {
  const all     = await getAllSmsQueue();
  const pending = all.filter(e => e.status === 'pending');
  let sent = 0, failed = 0;

  for (const entry of pending) {
    try {
      const { localId, status, createdAt, ...payload } = entry;
      const res = await api.post('/sms/send', payload);
      if (res.data?.success) {
        await updateSmsQueueEntry(localId, { status: 'sent', sentAt: new Date().toISOString() });
        sent++;
      } else {
        await updateSmsQueueEntry(localId, { status: 'failed' });
        failed++;
      }
    } catch {
      await updateSmsQueueEntry(entry.localId, { status: 'failed' });
      failed++;
    }
  }

  await clearSyncedSms();
  return { sent, failed };
}

/**
 * Refresh all local caches from API.
 * Call on login and when coming back online.
 */
export async function refreshAllCaches(api) {
  const results = { farmers: false, rateChartCow: false, rateChartBuf: false };

  // Farmers
  try {
    const res = await api.get('/farmer?pageSize=9999');
    if (res.data?.success) {
      const list = (res.data.data?.data || res.data.data || []).map(f => ({
        farmerId:    f.farmerId,
        farmerCode:  (f.farmerCode || '').toUpperCase(),
        farmerName:  f.farmerName || `${f.firstName} ${f.lastName}`,
        firstName:   f.firstName,
        lastName:    f.lastName,
        milkTypeId:  f.milkTypeId,
        milkTypeName:f.milkTypeName,
        isActive:    f.isActive,
      }));
      await saveFarmers(list);
      results.farmers = true;
    }
  } catch { /* keep old cache */ }

  // Rate charts  — cow (1) and buffalo (2)
  for (const milkTypeId of [1, 2]) {
    try {
      const res = await api.get(`/ratechart/${milkTypeId}`);
      if (res.data?.success && res.data.data) {
        const d = res.data.data;
        await saveRateChart(milkTypeId, {
          chartType:       d.chartType,
          startingAmount:  d.startingAmount,
          adjustmentValue: d.adjustmentValue,
          fatSteps:        d.fatSteps || [],
          snfSteps:        d.snfSteps || [],
        });
        if (milkTypeId === 1) results.rateChartCow = true;
        else                   results.rateChartBuf = true;
      }
    } catch { /* keep old cache */ }
  }

  await saveSetting('lastCacheRefresh', new Date().toISOString());
  return results;
}
